import React, { useState } from 'react';
import { Box, Typography, TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PromotedVendors from './PromotedVendors';
import PromotedServices from './PromotedServices';

const Promotions: React.FC = () => {
  // Feature flag to show/hide Services section
  const showServices = false;
  
  const [activeTab, setActiveTab] = useState<'vendors' | 'services'>('vendors');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);

  const tabs = [
    { key: 'vendors', label: 'Vendors' },
    ...(showServices ? [{ key: 'services', label: 'Services' }] : []),
  ];

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: 'var(--palette-light-6-value) var(--palette-light-4-value)',
          sm: 'var(--palette-light-10-value) var(--palette-light-10-value)',
          md: 'var(--palette-light-10-value) var(--palette-light-20-value)',
        },
        gap: 'var(--palette-light-6-value)',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--palette-light-6-value)',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: 'var(--palette-light-font-size-h1-value)',
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-1%',
            color: 'var(--palette-light-text-primary-value-hex)',
          }}
        >
          Promotion
        </Typography>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', flexWrap: 'wrap' }}>
          {tabs.map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--palette-light-2-value)',
                padding: '10px 20px 7px',
                height: '39px',
                backgroundColor:
                  tab.key === activeTab
                    ? 'var(--palette-light-brand-secondary-value-hex)'
                    : 'var(--palette-light-background-secondary-value-hex)',
                borderRadius: 'var(--palette-light-4-value)',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color:
                    tab.key === activeTab
                      ? 'var(--palette-light-text-foreground-value-hex)'
                      : 'var(--palette-light-text-primary-value-hex)',
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Subtitle */}
      <Typography
        sx={{
          fontSize: 'var(--palette-light-font-size-default-value)',
          color: 'var(--palette-light-text-primary-value-hex)',
          flexShrink: 0,
        }}
      >
        {activeTab === 'vendors'
          ? 'Manage promoted vendors at the home screen of the app. Drag and drop to reorder.'
          : showServices
          ? "Manage promoted services for 'We recommend' section at application cart screen. Drag and drop to reorder."
          : 'Manage promoted vendors at the home screen of the app. Drag and drop to reorder.'}
      </Typography>

      {/* Search and Add Button */}
      <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', alignItems: 'center', flexShrink: 0 }}>
        <TextField
          placeholder={activeTab === 'vendors' ? 'Search promoted vendors' : 'Search promoted services'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '420px',
            '& .MuiOutlinedInput-root': {
              height: '56px',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: 'var(--palette-light-3-value)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />

        {activeTab === 'vendors' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddVendorModal(true)}
            sx={{
              height: '56px',
              textTransform: 'none',
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              borderRadius: 'var(--palette-light-3-value)',
              padding: '0 var(--palette-light-6-value)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            Add Vendor
          </Button>
        )}
      </Box>

      {/* Content Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', width: '100%', flex: 1, overflow: 'hidden' }}>
        {activeTab === 'vendors' && (
          <PromotedVendors 
            searchQuery={searchQuery} 
            showAddVendorModal={showAddVendorModal}
            onCloseAddVendorModal={() => setShowAddVendorModal(false)}
          />
        )}
        {showServices && activeTab === 'services' && <PromotedServices searchQuery={searchQuery} />}
      </Box>
    </Box>
  );
};

export default Promotions;

